import { apiClient } from './api'
import type { ApiResponse } from '@/types/common'

// --- Staff management types ------------------------------------------------

export interface StaffUser {
  id: string
  name: string
  email: string
  role: 'admin' | 'super_admin'
  phone?: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
  lastLoginAt?: string | null
}

export interface CreateStaffPayload {
  name: string
  email: string
  password: string
  role: 'admin' | 'super_admin'
  phone?: string
  permissionCodes?: string[]
}

export interface Instructor {
  id: string
  name: string
  email: string
  phone?: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateInstructorPayload {
  name: string
  email: string
  password: string
  phone?: string
}

export interface Permission {
  id: string
  code: string
  module: string
  description: string
}

export interface PermissionsByModule {
  modules: Record<string, Permission[]>
  total: number
}

export interface UserPermissionsPayload {
  userId: string
  permissions: string[]
}

export interface AuthSettings {
  otpLength: number
  otpExpiryMinutes: number
  otpMaxRetries: number
  otpResendCooldownSeconds: number
}

export interface UpdateAuthSettingsPayload {
  otpExpiryMinutes?: number
  otpMaxRetries?: number
  otpResendCooldownSeconds?: number
}

export const adminService = {
  listPermissions: async (): Promise<ApiResponse<PermissionsByModule>> => {
    return apiClient.get<PermissionsByModule>('/admin/permissions')
  },

  getUserPermissions: async (userId: string): Promise<ApiResponse<UserPermissionsPayload>> => {
    return apiClient.get<UserPermissionsPayload>(`/admin/users/${userId}/permissions`)
  },

  updateUserPermissions: async (
    userId: string,
    permissions: string[],
  ): Promise<ApiResponse<UserPermissionsPayload>> => {
    return apiClient.patch<UserPermissionsPayload>(`/admin/users/${userId}/permissions`, {
      permissions,
    })
  },

  getAuthSettings: async (): Promise<ApiResponse<AuthSettings>> => {
    return apiClient.get<AuthSettings>('/admin/settings/auth')
  },

  updateAuthSettings: async (
    payload: UpdateAuthSettingsPayload,
  ): Promise<ApiResponse<{ message: string }>> => {
    return apiClient.patch<{ message: string }>('/admin/settings/auth', payload)
  },

  // --- Staff management ---------------------------------------------------
  listStaff: async (): Promise<ApiResponse<StaffUser[]>> => {
    return apiClient.get<StaffUser[]>('/admin/staff')
  },
  createStaff: async (payload: CreateStaffPayload): Promise<ApiResponse<StaffUser>> => {
    return apiClient.post<StaffUser>('/admin/staff', payload)
  },
  promoteStaff: async (id: string): Promise<ApiResponse<StaffUser>> => {
    return apiClient.patch<StaffUser>(`/admin/staff/${id}/promote`)
  },
  demoteStaff: async (id: string): Promise<ApiResponse<StaffUser>> => {
    return apiClient.patch<StaffUser>(`/admin/staff/${id}/demote`)
  },
  deactivateStaff: async (id: string): Promise<ApiResponse<StaffUser>> => {
    return apiClient.patch<StaffUser>(`/admin/staff/${id}/deactivate`)
  },
  reactivateStaff: async (id: string): Promise<ApiResponse<StaffUser>> => {
    return apiClient.patch<StaffUser>(`/admin/staff/${id}/reactivate`)
  },

  // --- Instructor management ----------------------------------------------
  listInstructors: async (): Promise<ApiResponse<Instructor[]>> => {
    return apiClient.get<Instructor[]>('/admin/instructors')
  },
  createInstructor: async (payload: CreateInstructorPayload): Promise<ApiResponse<Instructor>> => {
    return apiClient.post<Instructor>('/admin/instructors', payload)
  },
}
